/**
 * @fileoverview Module for `src/CMSUtil.js`.
 */

import axios from 'axios';
import moment from 'moment';
import deprecatedConfig from './lib/deprecatedConfig';

/**
 * CMS utility helpers for loading and sorting blog/content manifests.
 */

/**
 * Compares two post records using field-aware sort semantics.
 *
 * @param {Object} a - First post.
 * @param {Object} b - Second post.
 * @param {string} field - Field to compare.
 * @param {string} [direction='asc'] - Sort direction.
 * @returns {number} Sort comparison value.
 */
function comparePosts(a, b, field, direction = 'asc') {
  if (a[field] === b[field]) {
    return 0;
  }
  let firstField = a[field];
  let secondField = b[field];
  if (direction.toLowerCase() === 'desc') {
    firstField = b[field];
    secondField = a[field];
  }
  if (field.match(/_(?:at|date)$/)) {
    return (moment(firstField).isBefore(moment(secondField))) ? -1 : 1;
  } else if (isNaN(firstField)) {
    return firstField.localeCompare(secondField);
  } else {
    return (firstField > secondField) ? 1 : -1;
  }
}

/**
 * Gets the local branch override for CMS content fetches.
 *
 * @returns {string|undefined} Branch override when configured.
 */
export function getBranchOverride() {
  const branchOverride = localStorage.getItem('cmsBranchName');
  return branchOverride || undefined;
}

/**
 * Fetches HTML content for one post from CMS object storage.
 *
 * @param {Object} post - Post manifest entry containing `location`.
 * @returns {Promise<string|undefined>} HTML content when available.
 */
async function getPostHTML(post) {
  if (post) {
    const url = await deprecatedConfig.get('cmsApiUrl');
    const res = await axios({
      method: 'GET',
      url: `${ url }/${ post.location }`,
    });
    return res && res.data ? res.data : undefined;
  }
}

/**
 * Sorts posts using a `field:direction` key.
 *
 * @param {Object} params
 * @param {Object[]} params.posts - Post list.
 * @param {string} params.sortBy - Sort descriptor like `posted_at:desc`.
 * @returns {Object[]} Sorted posts.
 */
export function sortPosts({ posts, sortBy }) {
  const [sortByField, direction] = sortBy.split(':');
  return [...posts].sort((b, a) => comparePosts(b, a, sortByField || 'posted_at', direction));
}

/**
 * Filters posts by optional slug and tag constraints.
 *
 * @param {Object} params
 * @param {Object[]} params.posts - Post list.
 * @param {string} [params.slug] - Slug filter.
 * @param {string} [params.tag] - Tag filter.
 * @returns {Object[]} Filtered posts.
 */
export function filterPosts({ posts, slug, tag }) {
  return posts.filter((p) => {
    if (tag && p.tags.indexOf(tag) < 0) {
      return false;
    }
    return !(slug && p.slug !== slug);
  });
}

/**
 * Fetches CMS manifest data, applies filters, and optionally hydrates post HTML.
 *
 * @param {Object} params
 * @param {string} params.sortBy - Sort descriptor like `posted_at:desc`.
 * @param {string} [params.tag] - Tag filter.
 * @param {string} [params.slug] - Slug filter.
 * @returns {Promise<Object[]>} Matching post list.
 */
export async function getPosts({ sortBy, tag, slug = undefined }) {
  let posts;
  const [url, branchName] = await Promise.all([deprecatedConfig.get('cmsApiUrl'), deprecatedConfig.get('defaultCmsBranchName')]);
  const res = await axios({
    method: 'GET',
    url: `${ url }/branches/${ getBranchOverride() || branchName }/manifest.json?nocache=${ (new Date()).getTime() }`,
  });
  posts = res && res.data ? res.data : [];
  posts = filterPosts({ posts, slug, tag });
  let html;
  if (posts.length === 1) {
    html = await getPostHTML(posts[0]);
  } else {
    return sortPosts({ posts, sortBy });
  }
  if (html) {
    posts[0].html = html;
  }
  return posts;
}
